import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import type {
  Contact,
  ContactCadenceEnrollment,
  ContactCommercialSummary,
  ContactConsent,
  ContactConsentEvent,
  ContactConversation,
  ContactInput,
  ContactListItem,
  ContactTimelineResponse,
  CustomFieldDef,
  DuplicateLeadGroup,
  ImportContactsInput,
  ImportContactsPreview,
  ImportContactsResult,
  KanbanStage,
  LeadsFilters,
  MergeContactsInput,
  RegisterConsentInput,
  Tag,
  TagScope,
} from '@/types/leads'
import type { PrivacyRequestDetail } from '@/types/privacy'

export const leadsKeys = {
  list: (orgId: string | null, filters?: Partial<LeadsFilters>) =>
    ['contacts', 'list', orgId, filters ?? {}] as const,
  detail: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'detail', orgId, contactId] as const,
  tags: (orgId: string | null) => ['contacts', 'tags', orgId] as const,
  stages: (orgId: string | null) =>
    ['contacts', 'kanban-stages', orgId] as const,
  duplicates: (orgId: string | null) =>
    ['contacts', 'duplicates', orgId] as const,
  customFields: (orgId: string | null) =>
    ['contacts', 'custom-fields', orgId] as const,
  summary: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'summary', orgId, contactId] as const,
  timeline: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'timeline', orgId, contactId] as const,
  conversations: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'conversations', orgId, contactId] as const,
  cadences: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'cadences', orgId, contactId] as const,
  consent: (orgId: string | null, contactId: string | null) =>
    ['contacts', 'consent', orgId, contactId] as const,
}

function cleanParams(filters: Partial<LeadsFilters>) {
  const params: Record<string, string> = {
    page: String(filters.page ?? 1),
    limit: String(filters.limit ?? 500),
  }
  if (filters.search?.trim()) params.search = filters.search.trim()
  if (filters.tagId) params.tagId = filters.tagId
  if (filters.stageId) params.stageId = filters.stageId
  if (filters.hasOpenConversation && filters.hasOpenConversation !== 'all') {
    params.hasOpenConversation = filters.hasOpenConversation
  }
  return params
}

export function useLeads(
  orgId: string | null,
  filters: Partial<LeadsFilters> = {},
  enabled = true
) {
  return useQuery<PaginatedResponse<ContactListItem>>({
    queryKey: leadsKeys.list(orgId, filters),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<ContactListItem>>(
        `/orgs/${orgId}/contacts`,
        { params: cleanParams(filters) }
      )
      const meta = res.data.meta
      return {
        ...res.data,
        total: res.data.total ?? meta?.total ?? res.data.data.length,
        page: res.data.page ?? meta?.page ?? filters.page ?? 1,
        limit: res.data.limit ?? meta?.limit ?? filters.limit ?? 500,
      }
    },
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

export function useLeadDetail(orgId: string | null, contactId: string | null) {
  return useQuery<Contact>({
    queryKey: leadsKeys.detail(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<Contact>(`/orgs/${orgId}/contacts/${contactId}`)
      return res.data
    },
    enabled: !!orgId && !!contactId,
    staleTime: 30_000,
  })
}

export function useLeadSummary(orgId: string | null, contactId: string | null) {
  return useQuery<ContactCommercialSummary>({
    queryKey: leadsKeys.summary(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<ContactCommercialSummary>(
        `/orgs/${orgId}/contacts/${contactId}/summary`
      )
      return res.data
    },
    enabled: !!orgId && !!contactId,
    staleTime: 20_000,
  })
}

export function useLeadTimeline(
  orgId: string | null,
  contactId: string | null,
  enabled = true
) {
  return useQuery<ContactTimelineResponse>({
    queryKey: leadsKeys.timeline(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<ContactTimelineResponse>(
        `/orgs/${orgId}/contacts/${contactId}/timeline`,
        { params: { limit: 50 } }
      )
      return res.data
    },
    enabled: !!orgId && !!contactId && enabled,
    staleTime: 20_000,
  })
}

export function useContactConsent(
  orgId: string | null,
  contactId: string | null,
  enabled = true
) {
  return useQuery<ContactConsent>({
    queryKey: leadsKeys.consent(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<ContactConsent>(
        `/orgs/${orgId}/contacts/${contactId}/consent`
      )
      return res.data
    },
    enabled: !!orgId && !!contactId && enabled,
    staleTime: 20_000,
  })
}

export function useRegisterConsent(
  orgId: string | null,
  contactId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RegisterConsentInput) => {
      const res = await api.post<ContactConsentEvent>(
        `/orgs/${orgId}/contacts/${contactId}/consent`,
        input
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: leadsKeys.consent(orgId, contactId),
      })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, contactId),
      })
      void qc.invalidateQueries({
        queryKey: leadsKeys.timeline(orgId, contactId),
      })
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
    },
  })
}

export function useLeadConversations(
  orgId: string | null,
  contactId: string | null,
  enabled = true
) {
  return useQuery<ContactConversation[]>({
    queryKey: leadsKeys.conversations(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<ContactConversation[]>(
        `/orgs/${orgId}/contacts/${contactId}/conversations`
      )
      return res.data
    },
    enabled: !!orgId && !!contactId && enabled,
    staleTime: 20_000,
  })
}

export function useLeadCadences(
  orgId: string | null,
  contactId: string | null,
  enabled = true
) {
  return useQuery<ContactCadenceEnrollment[]>({
    queryKey: leadsKeys.cadences(orgId, contactId),
    queryFn: async () => {
      const res = await api.get<ContactCadenceEnrollment[]>(
        `/orgs/${orgId}/contacts/${contactId}/cadence-enrollments`
      )
      return res.data
    },
    enabled: !!orgId && !!contactId && enabled,
    staleTime: 20_000,
  })
}

export function useTags(orgId: string | null) {
  return useQuery<Tag[]>({
    queryKey: leadsKeys.tags(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Tag>>(`/orgs/${orgId}/tags`, {
        params: { limit: 500 },
      })
      return res.data.data
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateTag(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      color?: string
      scope?: TagScope
    }) => {
      const res = await api.post<Tag>(`/orgs/${orgId}/tags`, body)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.tags(orgId) })
    },
  })
}

export function useUpdateTag(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string
      body: { name?: string; color?: string; scope?: TagScope }
    }) => {
      const res = await api.put<Tag>(`/orgs/${orgId}/tags/${id}`, body)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.tags(orgId) })
    },
  })
}

export function useDeleteTag(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${orgId}/tags/${id}`)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.tags(orgId) })
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
    },
  })
}

export function useKanbanStages(orgId: string | null) {
  return useQuery<KanbanStage[]>({
    queryKey: leadsKeys.stages(orgId),
    queryFn: async () => {
      const res = await api.get<KanbanStage[]>(
        `/orgs/${orgId}/contacts/kanban-stages`
      )
      return res.data
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDuplicateLeads(orgId: string | null) {
  return useQuery<DuplicateLeadGroup[]>({
    queryKey: leadsKeys.duplicates(orgId),
    queryFn: async () => {
      const res = await api.get<DuplicateLeadGroup[]>(
        `/orgs/${orgId}/contacts/duplicates`
      )
      return res.data
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useMergeContacts(orgId: string | null) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: MergeContactsInput) => {
      const res = await api.post(`/orgs/${orgId}/contacts/merge`, input)
      return res.data
    },
    onSuccess: (_contact, variables) => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({ queryKey: leadsKeys.duplicates(orgId) })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, variables.primaryContactId),
      })
    },
    onError: () => {
      toast.error('Falha ao mesclar leads. Revise os dados e tente novamente.')
    },
  })
}

export function useCustomFields(orgId: string | null, enabled = true) {
  return useQuery<CustomFieldDef[]>({
    queryKey: leadsKeys.customFields(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<CustomFieldDef>>(
        `/orgs/${orgId}/custom-fields`,
        { params: { limit: 500 } }
      )
      return res.data.data
    },
    enabled: !!orgId && enabled,
    staleTime: 5 * 60_000,
  })
}

export function useCreateContact(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: ContactInput) => {
      const res = await api.post<Contact>(`/orgs/${orgId}/contacts`, body)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
    },
    onError: () => {
      toast.error('Falha ao criar contato. Tente novamente.')
    },
  })
}

export function useUpdateContact(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      body,
    }: {
      contactId: string
      body: ContactInput
    }) => {
      const res = await api.put<Contact>(
        `/orgs/${orgId}/contacts/${contactId}`,
        body
      )
      return res.data
    },
    onSuccess: (contact) => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, contact.id),
      })
    },
    onError: () => {
      toast.error('Falha ao atualizar contato. Tente novamente.')
    },
  })
}

/**
 * Excluir um contato abre uma solicitação LGPD de exclusão no servidor (contato,
 * conversas, mídias, payloads…). Normalmente termina na própria chamada; se o
 * orçamento inline acabar, o status volta PENDING e o restante roda em
 * segundo plano.
 */
export function useDeleteContact(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactId: string) => {
      const res = await api.delete<PrivacyRequestDetail>(
        `/orgs/${orgId}/contacts/${contactId}`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({ queryKey: ['privacy-requests', orgId] })
    },
  })
}

export function useDeleteContacts(orgId: string | null) {
  const deleteContact = useDeleteContact(orgId)
  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      const results: PrivacyRequestDetail[] = []
      for (const id of contactIds) {
        results.push(await deleteContact.mutateAsync(id))
      }
      return results
    },
  })
}

export function useAddTag(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      tagId,
    }: {
      contactId: string
      tagId: string
    }) => {
      const res = await api.post<Contact>(
        `/orgs/${orgId}/contacts/${contactId}/tags`,
        { tagId }
      )
      return res.data
    },
    onSuccess: (contact) => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, contact.id),
      })
    },
  })
}

export function useRemoveTag(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      tagId,
    }: {
      contactId: string
      tagId: string
    }) => {
      await api.delete(`/orgs/${orgId}/contacts/${contactId}/tags/${tagId}`)
      return { contactId }
    },
    onSuccess: ({ contactId }) => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, contactId),
      })
    },
  })
}

export function useUpdateKanbanStage(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      stageId,
    }: {
      contactId: string
      stageId: string | null
    }) => {
      const res = await api.put(
        `/orgs/${orgId}/contacts/${contactId}/kanban-stage`,
        {
          stageId,
        }
      )
      return res.data
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, variables.contactId),
      })
      void qc.invalidateQueries({
        queryKey: leadsKeys.summary(orgId, variables.contactId),
      })
      void qc.invalidateQueries({
        queryKey: leadsKeys.timeline(orgId, variables.contactId),
      })
    },
  })
}

export function useUpdateCustomFields(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      fields,
    }: {
      contactId: string
      fields: Array<{ customFieldId: string; value: string }>
    }) => {
      const res = await api.put<Contact>(
        `/orgs/${orgId}/contacts/${contactId}/custom-fields`,
        { fields }
      )
      return res.data
    },
    onSuccess: (contact) => {
      void qc.invalidateQueries({
        queryKey: leadsKeys.detail(orgId, contact.id),
      })
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
    },
    onError: () => {
      toast.error('Falha ao atualizar campos personalizados.')
    },
  })
}

export function useImportContacts(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ImportContactsInput) => {
      const res = await api.post<ImportContactsResult>(
        `/orgs/${orgId}/contacts/import`,
        input
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list', orgId] })
    },
  })
}

export function usePreviewImportContacts(orgId: string | null) {
  return useMutation({
    mutationFn: async (input: ImportContactsInput) => {
      const res = await api.post<ImportContactsPreview>(
        `/orgs/${orgId}/contacts/import/preview`,
        input
      )
      return res.data
    },
  })
}

export function useExportContacts(orgId: string | null) {
  return useMutation({
    mutationFn: async (filters: Partial<LeadsFilters>) => {
      const response = await api.get<Blob>(`/orgs/${orgId}/contacts/export`, {
        params: cleanParams(filters),
        responseType: 'blob',
      })
      const disposition = response.headers['content-disposition'] as
        | string
        | undefined
      const fileName =
        disposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
        `leads-${new Date().toISOString().slice(0, 10)}.csv`
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')

      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
  })
}

export function useSyncRdStation(orgId: string | null) {
  return useMutation({
    mutationFn: async (contactId: string) => {
      const res = await api.post<{ synced?: true; skipped?: string }>(
        `/orgs/${orgId}/integrations/rd-station/contacts/${contactId}/sync`
      )
      return res.data
    },
    onError: () => {
      toast.error('Falha ao sincronizar com RD Station. Tente novamente.')
    },
  })
}
